"use client";

import { useCallback, useMemo } from "react";
import { useUrlFilters, countActiveFilters, type UrlFilterState } from "./use-url-filters";
import type { ErrorClassification } from "@/types/logs";
import type { TimeBucketGranularity } from "@/types/filters";

type ArrayFilterKey = "userIds" | "routes" | "methods" | "errorStatuses" | "statusCodes";

function bucketRangeEnd(start: Date, granularity: TimeBucketGranularity): Date {
  const end = new Date(start);
  if (granularity === "hour") end.setUTCHours(end.getUTCHours() + 1);
  else if (granularity === "day") end.setUTCDate(end.getUTCDate() + 1);
  else end.setUTCDate(end.getUTCDate() + 7);
  return end;
}

export function useLogFilters() {
  const { filters, setFilters, setFiltersLocal, clearFilters, isPending } = useUrlFilters();

  const toggleArrayValue = useCallback(
    <K extends ArrayFilterKey>(key: K, value: string | number) => {
      const current = ((filters[key] as Array<string | number> | undefined) ?? []).slice();
      const index = current.findIndex((v) => v === value);
      if (index >= 0) {
        current.splice(index, 1);
      } else {
        current.push(value);
      }
      setFilters({ [key]: current.length > 0 ? current : undefined } as Partial<UrlFilterState>);
    },
    [filters, setFilters],
  );

  const toggleRoute = useCallback((route: string) => toggleArrayValue("routes", route), [toggleArrayValue]);
  const toggleUser = useCallback((userId: string) => toggleArrayValue("userIds", userId), [toggleArrayValue]);
  const toggleMethod = useCallback((method: string) => toggleArrayValue("methods", method), [toggleArrayValue]);
  const toggleStatusCode = useCallback(
    (statusCode: number) => toggleArrayValue("statusCodes", statusCode),
    [toggleArrayValue],
  );
  const toggleErrorStatus = useCallback(
    (status: ErrorClassification) => toggleArrayValue("errorStatuses", status),
    [toggleArrayValue],
  );

  const toggleDateBucket = useCallback(
    (bucketStart: string, granularity: TimeBucketGranularity) => {
      const start = new Date(bucketStart);
      const alreadyActive =
        filters.updatedFrom?.toISOString() === start.toISOString() && Boolean(filters.updatedTo);

      if (alreadyActive) {
        setFilters({ updatedFrom: undefined, updatedTo: undefined });
        return;
      }

      setFilters({ updatedFrom: start, updatedTo: bucketRangeEnd(start, granularity) });
    },
    [filters, setFilters],
  );

  const removeFilter = useCallback(
    (key: keyof UrlFilterState) => {
      if (key === "createdFrom" || key === "createdTo") {
        setFilters({ createdFrom: undefined, createdTo: undefined, lookbackAll: undefined });
        return;
      }
      if (key === "updatedFrom" || key === "updatedTo") {
        setFilters({ updatedFrom: undefined, updatedTo: undefined });
        return;
      }
      if (key === "responseTimeMinMs" || key === "responseTimeMaxMs") {
        setFilters({ responseTimeMinMs: undefined, responseTimeMaxMs: undefined });
        return;
      }
      setFilters({ [key]: undefined } as Partial<UrlFilterState>);
    },
    [setFilters],
  );

  const activeCount = useMemo(() => countActiveFilters(filters), [filters]);

  return {
    filters,
    setFilters,
    setFiltersLocal,
    clearFilters,
    removeFilter,
    toggleRoute,
    toggleUser,
    toggleMethod,
    toggleStatusCode,
    toggleErrorStatus,
    toggleDateBucket,
    activeCount,
    isPending,
  };
}
